import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buildLabel, buildsSupported, type AppBuildView, type Platform } from '@1qa/shared';
import { api } from '@/lib/api';

const prefKey = (projectId: string) => `1qa.runBuild.${projectId}`;
const CHANGED = '1qa:run-build';

function readPref(projectId: string): string | null {
  try {
    return window.localStorage.getItem(prefKey(projectId)) || null;
  } catch {
    return null;
  }
}

function writePref(projectId: string, id: string | null) {
  try {
    if (id) window.localStorage.setItem(prefKey(projectId), id);
    else window.localStorage.removeItem(prefKey(projectId));
  } catch {
    /* storage unavailable: keep the choice for this page only */
  }
}

/** "2.4 (240) · app-release.apk" for pickers. */
export const buildOptionLabel = (b: AppBuildView) => {
  const label = buildLabel(b);
  return label === b.fileName ? label : `${label} · ${b.fileName}`;
};

/**
 * The app build the person runs a mobile project with, remembered per project in this browser and shared by
 * every Run button. null means the latest build (the API's default), also when the chosen one was deleted.
 */
export function useRunBuild(projectId: string | undefined, platform: Platform | undefined) {
  const enabled = !!projectId && !!platform && buildsSupported(platform);
  const builds = useQuery({ queryKey: ['builds', projectId], queryFn: () => api.builds(projectId!), enabled });
  const [stored, setStored] = useState<string | null>(() => (projectId ? readPref(projectId) : null));

  useEffect(() => {
    if (!projectId) return;
    setStored(readPref(projectId));
    const sync = (e: Event) => {
      if ((e as CustomEvent<string>).detail === projectId) setStored(readPref(projectId));
    };
    window.addEventListener(CHANGED, sync);
    return () => window.removeEventListener(CHANGED, sync);
  }, [projectId]);

  const set = useCallback(
    (id: string | null) => {
      if (!projectId) return;
      writePref(projectId, id);
      setStored(id);
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: projectId }));
    },
    [projectId],
  );

  const list = enabled ? (builds.data ?? []) : [];
  const appBuildId = stored && list.some((b) => b.id === stored) ? stored : null;
  return { enabled, builds: list, appBuildId, setAppBuildId: set };
}

export type RunBuildState = ReturnType<typeof useRunBuild>;
